/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Globe, Settings, List, Info, Plus, Trash2, Palette, Image as ImageIcon, Languages, Tag, Droplet, Edit2, GripVertical } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const AttributeForm = ({ viewOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { success, error, warning, info } = useNotification();
  
  // Helper to get translated input type label
  const getInputTypeLabel = (type) => {
    const key = type.toLowerCase().replace(/ /g, '_');
    return t(`settings.${key}`);
  };
  
  // All possible input types from PRD
  const INPUT_TYPES = [
    'Text Field',
    'Text Area',
    'Text Editor',
    'Date',
    'Date and Time',
    'Yes/No',
    'Multiple Select',
    'Dropdown',
    'Price',
    'Media Image',
    'Visual Swatch',
    'Text Swatch',
    'Shipping Package',
    'URL',
    'Country'
  ];
  
  // Validation types for text field
  const VALIDATION_TYPES = [
    'None',
    'Decimal Number',
    'Integer Number',
    'Email',
    'URL',
    'Letters',
    'Letters or Numbers'
  ];
  
  // Available locales for labels
  const AVAILABLE_LOCALES = [
    { code: 'en', name: t('profile.english'), flag: '🇺🇸' },
    { code: 'zh', name: t('profile.chinese'), flag: '🇨🇳' },
  ];
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    attribute_code: '',
    default_label: '',
    input_type: 'Text Field',
    is_required: 0,
    default_value: '',
    input_validation: 'None',
    is_length: 0,
    is_weight: 0,
    length_unit: '',
    weight_unit: '',
    currency_unit: '',
    is_system: 0,
    source: null,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Track immutability: on edit, these fields cannot be changed back
  const [immutableFields, setImmutableFields] = useState({
    is_length: false,
    is_weight: false,
    length_unit: false,
    weight_unit: false,
    currency_unit: false,
    input_type: false,
    input_validation: false
  });
  
  // State for currency data
  const [currencies, setCurrencies] = useState([]);
  
  // State for system parameters (weight_unit, length_unit default)
  const [systemParams, setSystemParams] = useState({ weight_unit: '', length_unit: '' });
  
  // State for multilingual labels
  const [labels, setLabels] = useState([
    { locale: 'en', label: '', isNew: false }
  ]);
  
  // State for attribute options
  const [options, setOptions] = useState([]);
  
  // State for new option form
  const [newOption, setNewOption] = useState({
    // value will be auto-generated as option_id by backend
    label: '',
    english_label: '',
    chinese_label: '',
    swatch_value: '',
    swatch_type: '',
    sort_order: 0
  });
  
  // Track which option is being edited (index, or null when adding new)
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Track real DB option IDs that were removed by the user
  const [deletedOptionIds, setDeletedOptionIds] = useState([]);
  
  // Drag-and-drop state for option reordering
  const [dragOptionIndex, setDragOptionIndex] = useState(null);
  
  // Attribute code is now manually entered by user
  // No auto-generation from default label

  // Load attribute data, labels and options if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      
      // Load basic attribute data
      Promise.all([
        fetch(`/api/settings/attributes/${id}`).then(res => {
          if (!res.ok) throw new Error(`Attribute API returned ${res.status}`);
          return res.json();
        }),
        // Load labels
        fetch(`/api/settings/attributes/${id}/labels`).then(res => {
          if (!res.ok) return { status: 'success', data: [] }; // Labels may not exist
          return res.json();
        }),
        // Load options (only for certain input types)
        fetch(`/api/settings/attributes/${id}/options`).then(res => {
          if (!res.ok) return { status: 'success', data: [] }; // Options may not exist
          return res.json();
        })
      ])
      .then(([attrRes, labelsRes, optionsRes]) => {
        // Process attribute data
        if (attrRes.status === 'success' && attrRes.data) {
          const data = attrRes.data;
          setFormData({
            attribute_code: data.attribute_code || '',
            default_label: data.default_label || '',
            input_type: data.input_type || 'Text Field',
            is_required: data.is_required || 0,
            default_value: data.default_value || '',
            input_validation: data.input_validation || 'None',
            is_length: data.is_length || 0,
            is_weight: data.is_weight || 0,
            length_unit: data.length_unit || '',
            weight_unit: data.weight_unit || '',
            currency_unit: data.currency_unit || '',
            is_system: data.is_system || 0,
            source: data.source || null,
          });
          
          // Set immutability flags
          setImmutableFields({
            is_length: !!(data.is_length),
            is_weight: !!(data.is_weight),
            length_unit: !!(data.length_unit), // 长度单位一旦设定不可修改
            weight_unit: !!(data.weight_unit), // 重量单位一旦设定不可修改
            currency_unit: data.input_type === 'Price' && data.is_system === 1, // 系统Price属性货币单位不可修改
            input_type: true, // Input type is always immutable on edit
            input_validation: data.input_validation && data.input_validation !== 'None'
          });
        } else {
          console.error('Attribute API returned error:', attrRes.message);
        }
        
        // Process labels
        if (labelsRes.status === 'success' && labelsRes.data) {
          // Initialize with English as default
          const loadedLabels = labelsRes.data.map(label => ({
            locale: label.locale,
            label: label.label,
            isNew: false
          }));
          
          // Ensure English label exists (default from formData.default_label)
          const hasEnglish = loadedLabels.some(l => l.locale === 'en');
          if (!hasEnglish && formData.default_label) {
            loadedLabels.push({
              locale: 'en',
              label: formData.default_label,
              isNew: false
            });
          }
          
          setLabels(loadedLabels);
        }
        
        // Process options
        if (optionsRes.status === 'success' && optionsRes.data) {
          setOptions(optionsRes.data.map(option => ({
            ...option,
            isNew: false
          })));
        }
      })
      .catch(err => {
        console.error('Failed to load attribute data:', err);
      })
      .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  // Load currencies and system default currency
  useEffect(() => {
    const loadCurrencyData = async () => {
      try {
        // Load currencies list
        const currenciesRes = await fetch('/api/settings/currencies');
        if (currenciesRes.ok) {
          const currenciesData = await currenciesRes.json();
          if (currenciesData.status === 'success') {
            setCurrencies(currenciesData.data);
          }
        }
        
        // Load system parameters (weight_unit, length_unit defaults)
        const paramsRes = await fetch('/api/settings/params');
        if (paramsRes.ok) {
          const paramsData = await paramsRes.json();
          if (paramsData.status === 'success') {
            setSystemParams(paramsData.data);
          }
        }
      } catch (err) {
        console.error('Failed to load currency data:', err);
      }
    };
    
    loadCurrencyData();
  }, []);
  


  // Helper functions for labels management
  const handleAddLabel = () => {
    // Find the next locale that hasn't been added yet
    const existingLocales = labels.map(l => l.locale);
    const nextLocale = AVAILABLE_LOCALES.find(locale => !existingLocales.includes(locale.code));
    
    if (nextLocale) {
      setLabels([...labels, { locale: nextLocale.code, label: '', isNew: true }]);
    } else {
      info(t('settings.all_languages_added'));
    }
  };
  
  const handleRemoveLabel = (index) => {
    if (labels[index].locale === 'en') {
      warning(t('settings.cannot_remove_english'));
      return;
    }
    const newLabels = [...labels];
    newLabels.splice(index, 1);
    setLabels(newLabels);
  };
  
  const handleLabelChange = (index, field, value) => {
    const newLabels = [...labels];
    newLabels[index] = { ...newLabels[index], [field]: value };
    setLabels(newLabels);
  };
  
  // Helper functions for options management
  const handleAddOption = () => {
    // Default label is always required
    if (!newOption.label.trim()) {
      error('Default label is required');
      return;
    }
    
    // Swatch value is required for Visual Swatch only
    if (formData.input_type === 'Visual Swatch' && !newOption.swatch_value.trim()) {
      error('Swatch value is required for visual swatch types');
      return;
    }
    
    // Language labels are optional - if not provided, will fallback to default label
    // For swatch types, we also need to ensure swatch_type is set
    if (formData.input_type === 'Visual Swatch' && newOption.swatch_value && !newOption.swatch_type) {
      error('Please select a swatch type (color or image) for visual swatch');
      return;
    }
    
    if (editingIndex !== null) {
      // --- EDITING EXISTING OPTION ---
      const updatedOptions = [...options];
      updatedOptions[editingIndex] = {
        ...updatedOptions[editingIndex],
        label: newOption.label,
        english_label: newOption.english_label,
        chinese_label: newOption.chinese_label,
        swatch_value: formData.input_type === 'Visual Swatch' ? newOption.swatch_value : null,
        swatch_type: formData.input_type === 'Visual Swatch' ? (newOption.swatch_type || 'color') : null,
      };
      setOptions(updatedOptions);
      cancelEdit();
      success('Option updated');
    } else {
      // --- ADDING NEW OPTION ---
      // Check for duplicate default labels within this attribute
      const hasDuplicate = options.some(opt => opt.label === newOption.label);
      
      if (hasDuplicate) {
        error('Default label must be unique within this attribute. Please use a different label.');
        return;
      }
      
      const newOptionWithId = {
        ...newOption,
        // Value will be set by backend as option_id
        value: `temp_${Date.now()}`, // Temporary value for UI
        option_id: Date.now(), // Temporary ID for UI
        isNew: true
      };
      
      setOptions([...options, newOptionWithId]);
      setNewOption({
        label: '',
        english_label: '',
        chinese_label: '',
        swatch_value: '',
        swatch_type: '',
        sort_order: options.length
      });
    }
  };
  
  // Start editing an existing option
  const handleEditOption = (index) => {
    const option = options[index];
    setNewOption({
      label: option.label || '',
      english_label: option.english_label || '',
      chinese_label: option.chinese_label || '',
      swatch_value: option.swatch_value || '',
      swatch_type: option.swatch_type || 'color',
      sort_order: index
    });
    setEditingIndex(index);
  };
  
  // Cancel editing and reset form
  const cancelEdit = () => {
    setEditingIndex(null);
    setNewOption({
      label: '',
      english_label: '',
      chinese_label: '',
      swatch_value: '',
      swatch_type: '',
      sort_order: 0
    });
  };
  
  const handleRemoveOption = (index) => {
    const removedOption = options[index];
    
    // If this option has already been saved to DB, track its ID for deletion
    if (!removedOption.isNew && removedOption.option_id) {
      setDeletedOptionIds(prev => [...prev, parseInt(removedOption.option_id)]);
    }
    
    // If currently editing the option being removed, cancel edit mode
    if (editingIndex === index) {
      cancelEdit();
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index since we removed an item before it
      setEditingIndex(editingIndex - 1);
    }
    
    const newOptions = [...options];
    newOptions.splice(index, 1);
    // Update sort order
    const updatedOptions = newOptions.map((opt, idx) => ({
      ...opt,
      sort_order: idx
    }));
    setOptions(updatedOptions);
  };
  
  // Drag-and-drop option reordering
  const handleOptionDragStart = (index) => {
    setDragOptionIndex(index);
  };
  
  const handleOptionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleOptionDrop = (targetIndex) => {
    if (dragOptionIndex === null || dragOptionIndex === targetIndex) {
      setDragOptionIndex(null);
      return;
    }
    
    const newOptions = [...options];
    const [movedItem] = newOptions.splice(dragOptionIndex, 1);
    newOptions.splice(targetIndex, 0, movedItem);
    
    // Update sort_order
    const reorderedOptions = newOptions.map((opt, idx) => ({
      ...opt,
      sort_order: idx
    }));
    setOptions(reorderedOptions);
    setDragOptionIndex(null);
    
    // Call API to persist sort order immediately
    const optionIds = reorderedOptions.map(opt => opt.option_id).filter(id => id && !id.toString().startsWith('temp_'));
    if (optionIds.length > 1) {
      fetch('/api/settings/attribute-options/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_ids: optionIds })
      }).catch(err => console.error('Failed to reorder options:', err));
    }
  };
  
  const handleOptionDragEnd = () => {
    setDragOptionIndex(null);
  };
  
  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };
  
  // Save labels to API
  const saveLabels = async (attributeId) => {
    try {
      // Save each label
      const savePromises = labels.map(label => {
        const labelData = {
          attribute_id: attributeId,
          locale: label.locale,
          label: label.label.trim() || formData.default_label // Fallback to default label
        };
        
        return fetch('/api/settings/attribute-labels/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(labelData)
        });
      });
      
      await Promise.all(savePromises);
      return true;
    } catch (err) {
      console.error('Failed to save labels:', err);
      return false;
    }
  };
  
  // Save options to API
  const saveOptions = async (attributeId) => {
    try {
      // Step 1: Delete removed options from database first
      if (deletedOptionIds.length > 0) {
        const deletePromises = deletedOptionIds.map(optionId =>
          fetch(`/api/settings/attribute-options/${optionId}/delete`, {
            method: 'GET',
          }).then(async (res) => {
            const result = await res.json();
            if (result.status !== 'success') {
              throw new Error(`Delete option ${optionId} failed: ${result.message}`);
            }
          })
        );
        await Promise.all(deletePromises);
        setDeletedOptionIds([]);
      }
      
      // Step 2: Save each current option
      const savePromises = options.map((option, index) => {
        // Language labels are optional - fallback to default label if empty
        const englishLabel = option.english_label && option.english_label.trim() ? option.english_label.trim() : option.label;
        const chineseLabel = option.chinese_label && option.chinese_label.trim() ? option.chinese_label.trim() : option.label;
        
        const optionData = {
          attribute_id: parseInt(attributeId),
          // 不传 value，由后端自动设为 option_id
          default_label: option.label, // This is default_label
          english_label: englishLabel,
          chinese_label: chineseLabel,
          swatch_value: formData.input_type === 'Visual Swatch' ? (option.swatch_value || null) : null,
          swatch_type: formData.input_type === 'Visual Swatch' ? (option.swatch_type || null) : null,
          sort_order: index,
          ...(option.option_id && !option.isNew ? { option_id: parseInt(option.option_id) } : {})
        };
        
        return fetch('/api/settings/attribute-options/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(optionData)
        }).then(async (res) => {
          const result = await res.json();
          if (!res.ok) {
            throw new Error(`Option save failed: ${result.message || 'Unknown error'}`);
          }
          return result;
        });
      });
      
      await Promise.all(savePromises);
      return true;
    } catch (err) {
      console.error('Failed to save options:', err);
      return false;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    if (viewOnly) {
      navigate('/settings/attributes');
      return;
    }
    
    // Basic validation
    if (!formData.attribute_code.trim()) {
      error(t('settings.attribute_code_required'));
      return;
    }
    if (!formData.default_label.trim()) {
      error(t('settings.default_label_required'));
      return;
    }
    
    // is_length and is_weight are mutually exclusive
    if (formData.is_length === 1 && formData.is_weight === 1) {
      error('An attribute cannot be both length and weight at the same time');
      return;
    }
    
    // Currency unit is required for Price input type
    if (formData.input_type === 'Price' && !formData.currency_unit?.trim()) {
      error('Currency unit is required for Price attribute');
      return;
    }
    
    // Validate options for attribute types that require them
    const hasOptions = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(formData.input_type);
    if (hasOptions) {
      // Check if at least one option is provided
      if (options.length === 0) {
        error('At least one option is required for this attribute type');
        setActiveTab('options'); // Switch to options tab
        return;
      }
      
      // Validate each option
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        
        // Default label is always required
        if (!option.label || !option.label.trim()) {
          error(`Option ${i + 1}: Default label is required`);
          setActiveTab('options'); // Switch to options tab
          return;
        }
        
        // Swatch value is required for Visual Swatch only
        if (formData.input_type === 'Visual Swatch' && (!option.swatch_value || !option.swatch_value.trim())) {
          error(`Option ${i + 1}: Swatch value is required for visual swatch`);
          setActiveTab('options'); // Switch to options tab
          return;
        }
        
        // Swatch type is required for Visual Swatch when swatch_value is provided
        if (formData.input_type === 'Visual Swatch' && option.swatch_value && !option.swatch_type) {
          error(`Option ${i + 1}: Please select a swatch type (color or image) for visual swatch`);
          setActiveTab('options'); // Switch to options tab
          return;
        }
      }
    }
    
    const endpoint = id ? `/api/settings/attributes/${id}/edit` : '/api/settings/attributes/add';
    const method = id ? 'PUT' : 'POST';

    setIsSubmitting(true);
    try {
      // Save attribute first
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      
      if (res.ok && data.status === 'success') {
        // Get the attribute ID (either from response or use existing id)
        const attributeId = id || data.data?.attribute_id;
        
        if (attributeId) {
          // Save labels if there are any
          let labelsSaved = true;
          if (labels.length > 0) {
            labelsSaved = await saveLabels(attributeId);
          }
          
          // Save options if attribute type requires them
          let optionsSaved = true;
          const hasOptions = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(formData.input_type);
          if (hasOptions && options.length > 0) {
            optionsSaved = await saveOptions(attributeId);
          }
          
          if (labelsSaved && optionsSaved) {
            success(t('settings.save_success_attribute'));
            navigate('/settings/attributes');
          } else {
            warning(t('settings.save_partial_error'));
          }
        } else {
          success(t('settings.save_success_attribute'));
          navigate('/settings/attributes');
        }
      } else {
        error(t('settings.save_failed_attribute') + ': ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      error(t('settings.network_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center p-10">{t('settings.loading')}</div>;

  // Helper to determine if field should be shown
  const isTextField = formData.input_type === 'Text Field';
  const hasOptions = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'].includes(formData.input_type);
  const isSwatch = ['Visual Swatch', 'Text Swatch'].includes(formData.input_type);
  const isMediaImage = formData.input_type === 'Media Image';
  
  // Tabs configuration
  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: <Settings size={16} /> },
    { id: 'labels', label: t('settings.manage_labels'), icon: <Languages size={16} /> },
  ];
  
  // Add options tab only for input types that support options
  if (hasOptions) {
    tabs.push({ id: 'options', label: t('settings.manage_options'), icon: <Tag size={16} /> });
  }
  
  // Visual Swatch Picker component for attribute options
  const VisualSwatchPicker = ({ swatchValue, swatchType, onSwatchChange }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Determine display based on swatch type and value
    const getDisplayInfo = () => {
      if (!swatchValue) {
        return { type: 'empty', display: '/', color: 'transparent', image: null };
      }
      
      if (swatchType === 'color') {
        return { 
          type: 'color', 
          display: 'Color', 
          color: swatchValue, 
          image: null,
          hexCode: swatchValue.startsWith('#') ? swatchValue : `#${swatchValue}`
        };
      }
      
      if (swatchType === 'image') {
        return { 
          type: 'image', 
          display: 'Image', 
          color: 'transparent', 
          image: swatchValue,
          fileName: swatchValue.split('/').pop()
        };
      }
      
      // Fallback
      return { type: 'unknown', display: 'Unknown', color: 'transparent', image: null };
    };
    
    const displayInfo = getDisplayInfo();
    
    const handleColorPick = () => {
      const colorInput = document.createElement('input');
      colorInput.type = 'color';
      colorInput.value = '#3b82f6';
      colorInput.onchange = (e) => {
        onSwatchChange(e.target.value, 'color');
        setShowPicker(false);
      };
      colorInput.click();
    };
    
    const handleImageUpload = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };
    
    const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity', 'swatch');
        
        const res = await fetch('/api/media/upload', {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        
        if (data.status === 'success') {
          onSwatchChange(data.url, 'image');
        } else {
          console.error('Swatch upload failed:', data.message);
        }
      } catch (error) {
        console.error('Image upload failed:', error);
      } finally {
        setUploading(false);
        setShowPicker(false);
      }
    };
    
    const handleClear = () => {
      onSwatchChange('', '');
      setShowPicker(false);
    };
    
    return (
      <div className="relative">
        {/* Main swatch box with dropdown */}
        <div className="flex items-center gap-3">
          <div className="relative group">
            {/* Swatch display with improved empty state */}
            <div 
              className={`w-12 h-12 rounded-md border ${swatchValue ? 'border-slate-300 dark:border-slate-600' : 'border-dashed border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'} flex items-center justify-center cursor-pointer transition-all hover:scale-105 bg-white dark:bg-slate-800 overflow-hidden`}
              onClick={() => setShowPicker(!showPicker)}
              style={{
                backgroundColor: displayInfo.color,
                backgroundImage: displayInfo.image ? `url(${displayInfo.image})` : displayInfo.type === 'empty' ? 
                  'linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6), linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6)' : 'none',
                backgroundSize: displayInfo.type === 'empty' ? '10px 10px' : 'cover',
                backgroundPosition: displayInfo.type === 'empty' ? '0 0, 5px 5px' : 'center',
                boxShadow: swatchValue ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              {/* Empty state indicator */}
              {displayInfo.type === 'empty' && (
                <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400 dark:text-slate-500 group-hover:bg-slate-300 dark:group-hover:bg-slate-600 transition-colors">
                  <Plus size={12} />
                </div>
              )}
              
              {/* Uploading indicator */}
              {uploading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-md">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                </div>
              )}
              
              {/* Selected indicator */}
              {swatchValue && !uploading && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="w-5 h-5 bg-white/80 dark:bg-slate-800/80 rounded-full flex items-center justify-center">
                    <span className="text-xs text-slate-700 dark:text-slate-300">▼</span>
                  </div>
                </div>
              )}
            </div>
            

          </div>
          
          {/* Current value display */}
          <div className="flex-1 min-w-0">
            {swatchValue ? (
              <div className="space-y-1">
                <div className="font-medium text-sm text-slate-700 dark:text-slate-300 truncate">
                  {displayInfo.type === 'color' ? 'Color Swatch' : 'Image Swatch'}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate font-mono">
                  {displayInfo.type === 'color' ? displayInfo.hexCode : displayInfo.fileName || 'Image selected'}
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  Click to change
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="font-medium text-sm text-slate-500 dark:text-slate-400">
                  No swatch selected
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  Click to select color or upload image
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Hidden file input */}
        <input 
          type="file" 
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
        
        {/* Improved dropdown menu with better positioning */}
        {showPicker && (
          <div 
            className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-50 overflow-hidden"
            style={{
              boxShadow: '0 10px 25px rgba(0,0,0,0.1), 0 5px 10px rgba(0,0,0,0.05)'
            }}
          >
            <div className="py-1">
              <button
                type="button"
                onClick={handleColorPick}
                className="w-full px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-3 transition-colors group/color"
              >
                <div className="relative">
                  <div className="w-8 h-8 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center" style={{ backgroundColor: '#3b82f6' }} />
                  <div className="absolute inset-0 bg-black/0 group-hover/color:bg-black/10 rounded transition-colors"></div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-slate-700 dark:text-slate-300">Select Color</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Choose from color picker</div>
                </div>
              </button>
              <button
                type="button"
                onClick={handleImageUpload}
                className="w-full px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-3 transition-colors group/image"
              >
                <div className="relative">
                  <div className="w-8 h-8 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center bg-slate-100 dark:bg-slate-700">
                    <ImageIcon size={16} className="text-slate-500 dark:text-slate-400" />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 rounded transition-colors"></div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm text-slate-700 dark:text-slate-300">Select Image</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Upload to media/swatch/</div>
                </div>
              </button>
              <div className="border-t border-slate-100 dark:border-slate-700/50 my-1 mx-2"></div>
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-3 py-2.5 text-left hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-3 transition-colors"
              >
                <div className="w-8 h-8 rounded border border-red-200 dark:border-red-800 flex items-center justify-center">
                  <X size={16} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Clear Swatch</div>
                  <div className="text-xs text-red-500 dark:text-red-400/80 mt-0.5">Remove current selection</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{viewOnly ? t('settings.view_attribute') : (id ? t('settings.edit_attribute') : t('settings.create_attribute'))}</h1>
        <button onClick={() => navigate('/settings/attributes')} className="p-2 hover:bg-slate-200 rounded-full">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {/* Tab Navigation */}
        <div className="border-b border-slate-200 dark:border-slate-800">
          <nav className="-mb-px flex space-x-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className={`mt-6 ${viewOnly ? 'pointer-events-none select-none opacity-90' : ''}`}>
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.default_label')} <span className="text-red-500">*</span>
                  </label>
                  <input 
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    value={formData.default_label}
                    onChange={e => setFormData({...formData, default_label: e.target.value})}
                    placeholder="e.g., Color"
                    required
                  />
                  <p className="text-xs text-slate-400">{t('settings.default_label_hint')}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.attribute_code_unique')} <span className="text-red-500">*</span>
                  </label>
                  <input 
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formData.attribute_code}
                    onChange={e => setFormData({...formData, attribute_code: e.target.value})}
                    placeholder="e.g., color"
                    required
                    disabled={!!id}
                  />
                  <p className="text-xs text-slate-400">
                    {id ? 'Attribute code cannot be changed after creation' : 'Must be unique across the system'}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.input_type')} <span className="text-red-500">*</span>
                  </label>
                  <select 
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formData.input_type}
                    onChange={e => setFormData({...formData, input_type: e.target.value})}
                    disabled={immutableFields.input_type}
                  >
                    {INPUT_TYPES.map(type => (
                      <option key={type} value={type}>{getInputTypeLabel(type)}</option>
                    ))}
                  </select>
                  {immutableFields.input_type && <p className="text-xs text-amber-500">{t('settings.input_type_locked')}</p>}
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.values_required')}
                  </label>
                  <select 
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    value={formData.is_required}
                    onChange={e => setFormData({...formData, is_required: parseInt(e.target.value)})}
                  >
                    <option value={0}>{t('settings.no')}</option>
                    <option value={1}>{t('settings.yes')}</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t('settings.default_value')}
                </label>
                <input 
                  className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                  value={formData.default_value}
                  onChange={e => setFormData({...formData, default_value: e.target.value})}
                  placeholder="Default value for this attribute"
                />
              </div>
              
              {/* Conditional fields for Text Field */}
              {isTextField && (
                <div className="space-y-6">
                  <h3 className="text-md font-semibold border-b pb-2">Text Field Validation & Flags</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {t('settings.input_validation')}
                      </label>
                      <select 
                        className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        value={formData.input_validation}
                        onChange={e => setFormData({...formData, input_validation: e.target.value})}
                        disabled={immutableFields.input_validation}
                      >
                        {VALIDATION_TYPES.map(type => (
                          <option key={type} value={type}>
                            {t('settings.validation_' + type.toLowerCase().replace(/ /g, '_'))}
                          </option>
                        ))}
                      </select>
                      {immutableFields.input_validation && <p className="text-xs text-amber-500">{t('settings.input_validation_locked')}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {t('settings.is_length')}
                        {immutableFields.is_length && <span className="ml-1 text-xs text-amber-500">({t('common.locked')})</span>}
                      </label>
                      <select 
                        className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        value={formData.is_length}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          setFormData({...formData, is_length: val, is_weight: val === 1 ? 0 : formData.is_weight});
                        }}
                        disabled={immutableFields.is_length || formData.is_weight === 1}
                      >
                        <option value={0}>{t('settings.no')}</option>
                        <option value={1}>{t('settings.yes')}</option>
                      </select>
                      {formData.is_weight === 1 && <p className="text-xs text-amber-500">{t('settings.is_length_weight_exclusive')}</p>}
                      {immutableFields.is_length && formData.is_weight !== 1 && <p className="text-xs text-amber-500">{t('settings.is_length_locked')}</p>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {t('settings.is_weight')}
                        {immutableFields.is_weight && <span className="ml-1 text-xs text-amber-500">({t('common.locked')})</span>}
                      </label>
                      <select 
                        className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        value={formData.is_weight}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          setFormData({...formData, is_weight: val, is_length: val === 1 ? 0 : formData.is_length});
                        }}
                        disabled={immutableFields.is_weight || formData.is_length === 1}
                      >
                        <option value={0}>{t('settings.no')}</option>
                        <option value={1}>{t('settings.yes')}</option>
                      </select>
                      {formData.is_length === 1 && <p className="text-xs text-amber-500">{t('settings.is_length_weight_exclusive')}</p>}
                      {immutableFields.is_weight && formData.is_length !== 1 && <p className="text-xs text-amber-500">{t('settings.is_weight_locked')}</p>}
                    </div>
                  </div>

                  {/* Length unit — shown when is_length = Yes */}
                  {formData.is_length === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('settings.length_unit')}
                          {immutableFields.length_unit && <span className="ml-1 text-xs text-amber-500">({t('common.locked')})</span>}
                        </label>
                        <select
                          className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          value={formData.length_unit || ''}
                          onChange={e => setFormData({...formData, length_unit: e.target.value})}
                          disabled={immutableFields.length_unit}
                        >
                          <option value="">{systemParams.length_unit ? `(Default: ${systemParams.length_unit})` : t('settings.select_length_unit')}</option>
                          <option value="cm">Cm</option>
                          <option value="in">Inch</option>
                        </select>
                        {immutableFields.length_unit && <p className="text-xs text-amber-500">{t('settings.length_unit_locked')}</p>}
                      </div>
                    </div>
                  )}

                  {/* Weight unit — shown when is_weight = Yes */}
                  {formData.is_weight === 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('settings.weight_unit')}
                          {immutableFields.weight_unit && <span className="ml-1 text-xs text-amber-500">({t('common.locked')})</span>}
                        </label>
                        <select
                          className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          value={formData.weight_unit || ''}
                          onChange={e => setFormData({...formData, weight_unit: e.target.value})}
                          disabled={immutableFields.weight_unit}
                        >
                          <option value="">{systemParams.weight_unit ? `(Default: ${systemParams.weight_unit})` : t('settings.select_weight_unit')}</option>
                          <option value="kg">Kgs</option>
                          <option value="lbs">Lbs</option>
                        </select>
                        {immutableFields.weight_unit && <p className="text-xs text-amber-500">{t('settings.weight_unit_locked')}</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Currency unit selector for Price input type */}
              {formData.input_type === 'Price' && (
                <div className="space-y-6">
                  <h3 className="text-md font-semibold border-b pb-2">Price Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {t('settings.currency_unit')} <span className="text-red-500">*</span>
                        {immutableFields.currency_unit && <span className="ml-1 text-xs text-amber-500">({t('common.locked')})</span>}
                      </label>
                      <select 
                        className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        value={formData.currency_unit || ''}
                        onChange={e => setFormData({...formData, currency_unit: e.target.value})}
                        required
                        disabled={immutableFields.currency_unit}
                      >
                        <option value="">{t('settings.select_currency_unit')}</option>
                        {currencies.map(currency => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code} - {currency.name_en} ({currency.symbol})
                          </option>
                        ))}
                      </select>
                      {immutableFields.currency_unit && <p className="text-xs text-amber-500">{t('settings.currency_unit_locked')}</p>}
                    </div>
                  </div>
                </div>
              )}
              

              
              {/* System settings */}
              <div className="space-y-6">
                <h3 className="text-md font-semibold border-b pb-2">System Settings</h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('settings.is_system_attribute')}
                  </label>
                  <select 
                    className={`w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent ${id && formData.is_system === 1 ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-slate-500 dark:text-slate-400' : 'border-slate-300 dark:border-slate-700'}`}
                    value={formData.is_system}
                    onChange={e => setFormData({...formData, is_system: parseInt(e.target.value)})}
                    disabled={id && formData.is_system === 1}
                  >
                    <option value={0}>No (Regular attribute)</option>
                    <option value={1}>Yes (System attribute, cannot be deleted)</option>
                  </select>
                  <p className="text-xs text-slate-400">
                    {id && formData.is_system === 1 ? (
                      <span className="text-amber-600 dark:text-amber-400">
                        {t('attribute_form.system_warning')}
                      </span>
                    ) : (
                      'System attributes are visible to all users with view permissions, but cannot be deleted by anyone (including super administrators). Only editing is allowed.'
                    )}
                  </p>
                </div>

                {/* Source indicator */}
                {formData.source && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      {t('settings.source') || 'Source'}
                    </label>
                    <div className="flex items-center gap-2">
                      {formData.source === 'magento2' && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                          Auto-created from Magento 2
                        </span>
                      )}
                      {formData.source === 'system' && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          {t('settings.system_attr') || 'System Attribute'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      {formData.source === 'magento2'
                        ? 'This attribute was automatically created during Magento 2 attribute sync.'
                        : 'This attribute was created in the ERP system.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Manage Labels Tab */}
          {activeTab === 'labels' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">{t('settings.manage_labels')}</h3>
                <button
                  type="button"
                  onClick={handleAddLabel}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={16} />
                  {t('settings.add_label')}
                </button>
              </div>
              
              <div className="space-y-4">
                {labels.map((label, index) => (
                  <div key={index} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{
                          AVAILABLE_LOCALES.find(locale => locale.code === label.locale)?.flag || '🌐'
                        }</span>
                        <span className="font-medium">
                          {AVAILABLE_LOCALES.find(locale => locale.code === label.locale)?.name || label.locale}
                        </span>

                      </div>
                      
                      {label.locale !== 'en' && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLabel(index)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        {t('settings.label')}
                      </label>
                      <input
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                        value={label.label}
                        onChange={e => handleLabelChange(index, 'label', e.target.value)}
                        placeholder={label.locale === 'en' ? formData.default_label : t('settings.enter_label_for_locale')}
                        required={label.locale === 'en'}
                      />
                      {label.locale === 'en' && !label.label && formData.default_label && (
                        <p className="text-xs text-slate-400">
                          Will use default label: {formData.default_label}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                
                {labels.length === 0 && (
                  <div className="text-center p-8 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
                    <Languages size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                    <p className="text-slate-500 dark:text-slate-400">
                      {t('settings.no_labels_added')}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      {t('settings.add_labels_description')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Manage Options Tab */}
          {activeTab === 'options' && hasOptions && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">{t('settings.manage_options')}</h3>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {options.length} {t('settings.options')}
                </div>
              </div>
              
              {/* Add New Option Form */}
              <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <h4 className="font-medium mb-4">{t('settings.add_new_option')}</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Default Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                      value={newOption.label}
                      onChange={e => setNewOption({...newOption, label: e.target.value})}
                      placeholder={t('attribute_form.label_any_lang')}
                    />
                    <p className="text-xs text-slate-400">For database/Laminas backend</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      English Label
                    </label>
                    <input
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                      value={newOption.english_label}
                      onChange={e => setNewOption({...newOption, english_label: e.target.value})}
                      placeholder="e.g., Red"
                    />
                    <p className="text-xs text-slate-400">For English UI (optional - will use default label if empty)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Chinese Label
                    </label>
                    <input
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                      value={newOption.chinese_label}
                      onChange={e => setNewOption({...newOption, chinese_label: e.target.value})}
                      placeholder={t('attribute_form.label_zh')}
                    />
                    <p className="text-xs text-slate-400">For Chinese UI (optional - will use default label if empty)</p>
                  </div>
                </div>
                
                {/* Swatch fields for swatch types */}
                {isSwatch && (
                  <div className="space-y-4 mb-4">
                    {/* Different fields for Visual Swatch vs Text Swatch */}
                    {formData.input_type === 'Visual Swatch' ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            Swatch Value <span className="text-red-500">*</span>
                          </label>
                          
                          {/* Integrated Swatch Picker for Visual Swatch */}
                          <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800/30">
                            <VisualSwatchPicker 
                              swatchValue={newOption.swatch_value}
                              swatchType={newOption.swatch_type}
                              onSwatchChange={(value, type) => {
                                setNewOption({...newOption, 
                                  swatch_value: value,
                                  swatch_type: type
                                });
                              }}
                            />
                          </div>
                          
                          <p className="text-xs text-slate-400 mt-2">
                            Click the swatch box to select color or upload image. Images will be stored in media/swatch/ directory.
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Text Swatch - no swatch value needed */
                      <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          <Info size={16} className="inline mr-1" />
                          Text swatch options don't require a swatch value. The option label will be displayed as the swatch text.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex justify-end gap-2">
                  {editingIndex !== null && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                      editingIndex !== null
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {editingIndex !== null ? (
                      <><Save size={16} /> Update Option</>
                    ) : (
                      <><Plus size={16} /> {t('settings.add_option')}</>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Options List */}
              <div className="space-y-3">
                {options.map((option, index) => (
                  <div 
                    key={option.option_id || index} 
                    draggable
                    onDragStart={() => handleOptionDragStart(index)}
                    onDragOver={handleOptionDragOver}
                    onDrop={() => handleOptionDrop(index)}
                    onDragEnd={handleOptionDragEnd}
                    className={`p-4 border rounded-lg transition-all ${
                      dragOptionIndex === index 
                        ? 'border-blue-400 dark:border-blue-500 shadow-lg opacity-80 scale-105' 
                        : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30'
                    } ${dragOptionIndex !== null && dragOptionIndex !== index ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-3">
                        <span className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                          <GripVertical size={18} />
                        </span>
                        <span className="text-sm text-slate-400 font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            <span className="mr-3">EN: {option.english_label || option.label}</span>
                            <span>CN: {option.chinese_label || option.label}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Swatch preview for Visual Swatch only */}
                        {formData.input_type === 'Visual Swatch' && option.swatch_value && (
                          <div 
                            className="w-8 h-8 rounded border border-slate-300 dark:border-slate-600"
                            style={{
                              backgroundColor: option.swatch_type === 'color' ? option.swatch_value : 'transparent',
                              backgroundImage: option.swatch_type === 'image' ? `url(${option.swatch_value})` : 'none',
                              backgroundSize: 'cover'
                            }}
                            title={`Swatch: ${option.swatch_value}`}
                          />
                        )}
                        
                        <button
                          type="button"
                          onClick={() => handleEditOption(index)}
                          className="p-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                          title="Edit option"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Swatch details for Visual Swatch only */}
                    {formData.input_type === 'Visual Swatch' && option.swatch_value && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        Swatch: {option.swatch_value} {option.swatch_type && `(${option.swatch_type})`}
                      </div>
                    )}
                  </div>
                ))}
                
                {options.length === 0 && (
                  <div className="text-center p-8 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
                    <Tag size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                    <p className="text-slate-500 dark:text-slate-400">
                      {t('settings.no_options_added')}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      {t('settings.add_options_description')}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="text-sm text-slate-500 dark:text-slate-400 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="font-medium">💡 {t('settings.options_tip_title')}</p>
                <p className="mt-1">{t('settings.options_tip_description')}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Form actions with hint text */}
        <div className="flex justify-between items-center pt-6 border-t border-slate-200 dark:border-slate-800 mt-6">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {activeTab === 'basic' && t('settings.basic_info_tab_hint')}
            {activeTab === 'labels' && t('settings.labels_tab_hint')}
            {activeTab === 'options' && t('settings.options_tab_hint')}
          </div>
          
          <div className="flex gap-3">
            {viewOnly ? (
              <button 
                type="button" 
                onClick={() => navigate('/settings/attributes')} 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('common.close')}
              </button>
            ) : (
              <>
                <button 
                  type="button" 
                  onClick={() => navigate('/settings/attributes')} 
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('common.processing')}
                    </span>
                  ) : (
                    <><Save size={18} />{id ? t('common.update') : t('common.add')}</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AttributeForm;
